/**
 * Leave requests and company holidays.
 *
 * Leave is not only an attendance concern: approved *unpaid* leave is what
 * produces loss-of-pay days on a payslip. So the one thing this screen must
 * never do is let someone approve unpaid leave without noticing they have just
 * reduced a person's pay. That consequence is stated on the row, in the
 * confirmation, and in the empty state — a fortnight later on a payslip is too
 * late to find out.
 *
 * Holidays sit on the same screen because they answer the same question from
 * the other side: a day that is already a company holiday is not a day anyone
 * needs to book off.
 *
 * Three different permissions overlap here, so each control is gated to match
 * what the API allows rather than to match the page:
 *
 *   filing leave     - anyone signed in, for themselves
 *   approving        - managers and above
 *   editing holidays - company admin and the owner
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarDays, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { isCompanyAdmin, isSupervisor, useAuth } from '../store/auth';
import type { Holiday, LeaveRequest, LeaveStatus, LeaveType, Page } from '../types';

const TYPE_LABELS: Record<LeaveType, string> = {
  casual: 'Casual',
  sick: 'Sick',
  earned: 'Earned',
  unpaid: 'Unpaid',
  comp_off: 'Comp off',
};

const STATUS_TONE: Record<LeaveStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-500',
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Inclusive day count, which is the ceiling the API enforces on `days`. */
function spanDays(start: string, end: string): number {
  if (!start || !end) return 0;
  const from = new Date(start).getTime();
  const to = new Date(end).getTime();
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return 0;
  return Math.round((to - from) / 86_400_000) + 1;
}

export default function LeavePage() {
  const queryClient = useQueryClient();
  const me = useAuth((s) => s.user);
  const canApprove = isSupervisor(me);
  const canEditHolidays = isCompanyAdmin(me);

  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState<LeaveStatus | ''>('');
  const [deciding, setDeciding] = useState<{
    leave: LeaveRequest;
    action: 'approve' | 'reject' | 'cancel';
  } | null>(null);

  const requests = useQuery<Page<LeaveRequest>>({
    queryKey: ['leave', statusFilter],
    queryFn: async () =>
      (await client.get('/api/v1/leave', {
        params: { limit: 200, ...(statusFilter ? { status: statusFilter } : {}) },
      })).data,
  });

  const fileLeave = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post('/api/v1/leave', body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave'] });
      setShowForm(false);
      toast.success('Leave requested');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const rows = requests.data?.items ?? [];
  const pendingUnpaid = rows.filter((r) => r.status === 'pending' && r.unpaid).length;

  return (
    <div className="space-y-6">
      <section className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Leave</h2>
            <p className="mt-1 max-w-2xl text-xs text-gray-500">
              You see your own requests; a manager sees their team&rsquo;s, and an
              administrator the whole company. Only a pending request can be
              decided — once approved or rejected it stays that way, because an
              approved unpaid day may already have been docked on a payslip.
            </p>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowForm((v) => !v)}
          >
            <Plus className="h-4 w-4" />
            {showForm ? 'Cancel' : 'Request leave'}
          </button>
        </div>

        {canApprove && pendingUnpaid > 0 && (
          <p className="mt-3 flex items-start gap-1.5 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {pendingUnpaid} pending {pendingUnpaid === 1 ? 'request is' : 'requests are'}{' '}
            unpaid. Approving those reduces pay for the days taken.
          </p>
        )}

        <div className="mt-3">
          <label className="text-xs font-medium text-gray-600">
            Status
            <select
              className="input mt-1 block"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as LeaveStatus | '')}
            >
              <option value="">All</option>
              {(Object.keys(STATUS_TONE) as LeaveStatus[]).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {showForm && (
        <LeaveForm submitting={fileLeave.isPending} onSubmit={fileLeave.mutate} />
      )}

      {requests.isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : requests.isError ? (
        <div className="card flex items-center justify-between border border-red-200 bg-red-50">
          <p className="text-sm text-red-800">Could not load leave requests.</p>
          <button type="button" className="btn-ghost" onClick={() => void requests.refetch()}>
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card text-center">
          <CalendarDays className="mx-auto h-6 w-6 text-gray-300" />
          <p className="mt-2 text-sm text-gray-600">No leave requests to show.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th scope="col" className="px-4 py-2.5 font-medium">Who</th>
                <th scope="col" className="px-4 py-2.5 font-medium">Type</th>
                <th scope="col" className="px-4 py-2.5 font-medium">Dates</th>
                <th scope="col" className="px-4 py-2.5 font-medium">Days</th>
                <th scope="col" className="px-4 py-2.5 font-medium">Status</th>
                <th scope="col" className="px-4 py-2.5 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((leave) => {
                const mine = leave.userId === me?.id;
                return (
                  <tr key={leave.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-gray-900">
                      {leave.userName ?? (mine ? 'You' : leave.userId)}
                      {mine && leave.userName && (
                        <span className="ml-1.5 text-xs text-gray-400">you</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {TYPE_LABELS[leave.leaveType]}
                      {leave.unpaid && (
                        <span
                          className="badge ml-1.5 bg-amber-100 text-amber-700"
                          title="Approving this reduces the employee's pay"
                        >
                          affects pay
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {leave.startDate}
                      {leave.endDate !== leave.startDate && ` → ${leave.endDate}`}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-gray-600">
                      {leave.days}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`badge ${STATUS_TONE[leave.status]}`}>
                        {leave.status}
                      </span>
                      {leave.decisionNote && (
                        <p className="mt-0.5 text-xs text-gray-500">
                          {leave.decisionNote}
                        </p>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right">
                      {leave.status === 'pending' && (
                        <div className="flex justify-end gap-1">
                          {/* Approving is manager-and-above; the API refuses
                              anyone else, so the buttons are not shown. */}
                          {canApprove && (
                            <>
                              <button
                                type="button"
                                className="btn-ghost px-2 py-1 text-xs"
                                onClick={() => setDeciding({ leave, action: 'approve' })}
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                className="btn-ghost px-2 py-1 text-xs text-red-600"
                                onClick={() => setDeciding({ leave, action: 'reject' })}
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {/* Anyone may withdraw their own while it is pending. */}
                          {(mine || canApprove) && (
                            <button
                              type="button"
                              className="btn-ghost px-2 py-1 text-xs"
                              onClick={() => setDeciding({ leave, action: 'cancel' })}
                            >
                              Withdraw
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {deciding && (
        <DecisionDialog
          leave={deciding.leave}
          action={deciding.action}
          onClose={() => setDeciding(null)}
          onDone={() => {
            queryClient.invalidateQueries({ queryKey: ['leave'] });
            setDeciding(null);
          }}
        />
      )}

      <HolidaysSection canEdit={canEditHolidays} />
    </div>
  );
}

function LeaveForm({
  submitting,
  onSubmit,
}: {
  submitting: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({
    leaveType: 'casual' as LeaveType,
    startDate: today(),
    endDate: today(),
    days: '1',
    reason: '',
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const span = spanDays(form.startDate, form.endDate);
  const daysNum = Number(form.days);
  const tooMany = span > 0 && daysNum > span;
  const notAStep = form.days !== '' && (daysNum * 2) % 1 !== 0;

  return (
    <form
      className="card space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          leaveType: form.leaveType,
          startDate: form.startDate,
          endDate: form.endDate,
          days: form.days,
          reason: form.reason.trim() || null,
        });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="l-type">Type</label>
          <select
            id="l-type"
            className="input"
            value={form.leaveType}
            onChange={(e) => set('leaveType', e.target.value as LeaveType)}
          >
            {(Object.keys(TYPE_LABELS) as LeaveType[]).map((t) => (
              <option key={t} value={t}>{TYPE_LABELS[t]}</option>
            ))}
          </select>
          {form.leaveType === 'unpaid' && (
            <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Unpaid leave is docked from pay for the days taken, once approved.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label" htmlFor="l-from">From</label>
            <input
              id="l-from"
              type="date"
              className="input"
              value={form.startDate}
              onChange={(e) => {
                const startDate = e.target.value;
                setForm((f) => ({
                  ...f,
                  startDate,
                  // Keep the range coherent rather than letting the API reject it.
                  endDate: f.endDate < startDate ? startDate : f.endDate,
                }));
              }}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="l-to">To</label>
            <input
              id="l-to"
              type="date"
              className="input"
              value={form.endDate}
              min={form.startDate}
              onChange={(e) => set('endDate', e.target.value)}
              required
            />
          </div>
        </div>

        <div>
          <label className="label" htmlFor="l-days">Days</label>
          <input
            spellCheck={false}
            id="l-days"
            className="input font-mono"
            inputMode="decimal"
            value={form.days}
            onChange={(e) => set('days', e.target.value)}
            required
          />
          {/* Deliberately not derived from the range: a range can contain a
              holiday or a weekly off that should not be counted. */}
          <p className="mt-1 text-xs text-gray-500">
            {span > 0
              ? `${span}-day range. Enter fewer if it covers a holiday or a weekly off.`
              : 'Whole or half days.'}
          </p>
          {tooMany && (
            <p className="mt-1 text-xs text-red-600">
              Cannot claim more than the {span} days requested.
            </p>
          )}
          {notAStep && (
            <p className="mt-1 text-xs text-red-600">Must be a whole or half day.</p>
          )}
        </div>

        <div>
          <label className="label" htmlFor="l-reason">Reason</label>
          <input
            spellCheck={true}
            id="l-reason"
            className="input"
            value={form.reason}
            onChange={(e) => set('reason', e.target.value)}
          />
        </div>
      </div>

      <button
        type="submit"
        className="btn-primary"
        disabled={submitting || tooMany || notAStep || !daysNum}
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        Request leave
      </button>
    </form>
  );
}

/**
 * Confirming a decision.
 *
 * Approving unpaid leave is the one action here with a money consequence, so
 * it is spelled out at the moment of approving rather than left to the badge
 * on the row.
 */
function DecisionDialog({
  leave,
  action,
  onClose,
  onDone,
}: {
  leave: LeaveRequest;
  action: 'approve' | 'reject' | 'cancel';
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState('');

  const decide = useMutation({
    mutationFn: async () =>
      (await client.post(`/api/v1/leave/${leave.id}/${action}`, {
        note: note.trim() || null,
      })).data,
    onSuccess: () => {
      toast.success(
        action === 'approve'
          ? 'Leave approved'
          : action === 'reject'
            ? 'Leave rejected'
            : 'Request withdrawn',
      );
      onDone();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const verb =
    action === 'approve' ? 'Approve' : action === 'reject' ? 'Reject' : 'Withdraw';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold text-gray-900">
          {verb} {leave.days} day{leave.days === '1' ? '' : 's'} of{' '}
          {TYPE_LABELS[leave.leaveType].toLowerCase()} leave
        </h3>
        <p className="mt-0.5 text-xs text-gray-500">
          {leave.userName ?? 'This employee'} · {leave.startDate}
          {leave.endDate !== leave.startDate && ` → ${leave.endDate}`}
        </p>

        {action === 'approve' && leave.unpaid && (
          <div className="mt-3 rounded-lg bg-amber-50 p-3 ring-1 ring-amber-200">
            <p className="flex items-start gap-1.5 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              This is unpaid leave. Approving it docks {leave.days} day
              {leave.days === '1' ? '' : 's'} of pay from{' '}
              {leave.userName ?? 'this employee'}, and it cannot be undone here
              once decided.
            </p>
          </div>
        )}

        <div className="mt-3">
          <label className="label" htmlFor="d-note">
            Note {action === 'reject' && <span className="text-gray-400">— worth giving a reason</span>}
          </label>
          <textarea
            spellCheck={true}
            id="d-note"
            className="input"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={decide.isPending}
            onClick={() => decide.mutate()}
          >
            {decide.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {verb}
          </button>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Company holidays.
 *
 * Everyone can read them — a holiday is a day nobody needs to book off, and
 * attendance skips scheduling pings on one. Only an administrator can change
 * the list, so the add and remove controls are hidden from everyone else
 * rather than shown and left to fail.
 */
function HolidaysSection({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ holidayDate: today(), name: '', state: '' });

  const holidays = useQuery<Page<Holiday>>({
    queryKey: ['holidays'],
    queryFn: async () =>
      (await client.get('/api/v1/holidays', { params: { limit: 200 } })).data,
  });

  const addHoliday = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post('/api/v1/holidays', body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
      setShowForm(false);
      setForm({ holidayDate: today(), name: '', state: '' });
      toast.success('Holiday added');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const removeHoliday = useMutation({
    mutationFn: async (id: string) =>
      (await client.delete(`/api/v1/holidays/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
      toast.success('Holiday removed');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const rows = holidays.data?.items ?? [];

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Company holidays</h2>
          <p className="mt-1 text-xs text-gray-500">
            Attendance schedules no check-ins on a holiday, so time off is never
            scored as absence. A state can be named for a regional holiday.
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowForm((v) => !v)}
          >
            <Plus className="h-4 w-4" />
            {showForm ? 'Cancel' : 'Add holiday'}
          </button>
        )}
      </div>

      {canEdit && showForm && (
        <form
          className="card grid gap-3 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            addHoliday.mutate({
              holidayDate: form.holidayDate,
              name: form.name.trim(),
              state: form.state.trim() || null,
            });
          }}
        >
          <div>
            <label className="label" htmlFor="h-date">Date</label>
            <input
              id="h-date"
              type="date"
              className="input"
              value={form.holidayDate}
              onChange={(e) => setForm({ ...form, holidayDate: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="h-name">Name</label>
            <input
              spellCheck={false}
              id="h-name"
              className="input"
              placeholder="Ugadi"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              minLength={2}
            />
          </div>
          <div>
            <label className="label" htmlFor="h-state">State (optional)</label>
            <input
              spellCheck={false}
              id="h-state"
              className="input"
              placeholder="Whole company if blank"
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
            />
          </div>
          <div className="sm:col-span-3">
            <button type="submit" className="btn-primary" disabled={addHoliday.isPending}>
              {addHoliday.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Add holiday
            </button>
          </div>
        </form>
      )}

      {holidays.isLoading ? (
        <div className="flex justify-center py-6 text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <p className="card text-sm text-gray-600">
          No holidays recorded. Every working day will expect check-ins.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl bg-white ring-1 ring-gray-200">
          {rows.map((holiday) => (
            <li key={holiday.id} className="flex items-center justify-between px-4 py-2.5">
              <span className="text-sm text-gray-900">
                {holiday.name}
                <span className="ml-2 text-xs text-gray-500">{holiday.holidayDate}</span>
                {holiday.state && (
                  <span className="badge ml-2 bg-gray-100 text-gray-600">
                    {holiday.state}
                  </span>
                )}
              </span>
              {canEdit && (
                <button
                  type="button"
                  className="btn-ghost px-2 py-1 text-xs text-red-600"
                  title="Remove"
                  onClick={() => removeHoliday.mutate(holiday.id)}
                  disabled={removeHoliday.isPending}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
